import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

export type ThemeOption = "auto" | ThemeName;
export type ThemeName = "light" | "dark";

export interface ThemeContextValue {
  themeOption: ThemeOption;
  updateThemeOption: (theme: ThemeOption) => void;
  appliedTheme: ThemeName;
  isDarkMode: boolean;
  isLightMode: boolean;
  oppositeTheme: ThemeName;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** Export a custom hook to simplify using this context */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

const prefersColorSchemeDarkMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

/** A React context provider for managing the application's theme. */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeOption, setThemeOption] = useState<ThemeOption>(getStoredThemeOption() || "auto");
  const [preferredColorScheme, setPreferredColorScheme] = useState<ThemeName>(getPreferredColorScheme());

  // Use an effect to keep the user's preferred color scheme in sync
  useEffect(() => {
    const onPrefersColorSchemeChange = () => {
      setPreferredColorScheme(getPreferredColorScheme());
    };
    prefersColorSchemeDarkMediaQuery.addEventListener("change", onPrefersColorSchemeChange);
    return () => {
      prefersColorSchemeDarkMediaQuery.removeEventListener("change", onPrefersColorSchemeChange);
    };
  });

  // Calculate the applied theme based on the selected theme option
  const appliedTheme = themeOption === "auto" ? preferredColorScheme : themeOption;

  // Apply the theme to the document when it changes
  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", appliedTheme);
  }, [appliedTheme]);

  // Define a callback to update the selected theme option and save it to local storage
  const updateThemeOption = useCallback((themeOption: ThemeOption) => {
    setThemeOption(themeOption);
    setStoredThemeOption(themeOption);
  }, []);

  // Compute the remaining context values
  const isDarkMode = appliedTheme === "dark";
  const isLightMode = appliedTheme === "light";
  const oppositeTheme = appliedTheme === "dark" ? "light" : "dark";
  const contextValue: ThemeContextValue = {
    themeOption,
    appliedTheme,
    isDarkMode,
    isLightMode,
    oppositeTheme,
    updateThemeOption,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

function getStoredThemeOption(): ThemeOption | null {
  return localStorage.getItem("theme") as ThemeOption | null;
}

function setStoredThemeOption(theme: ThemeOption) {
  localStorage.setItem("theme", theme);
}

function getPreferredColorScheme(): ThemeName {
  return prefersColorSchemeDarkMediaQuery.matches ? "dark" : "light";
}
